<template>
  <v-dialog v-model="show" persistent="">
    <v-card>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>Create Extracts</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="show = false">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-container grid-list-md fluid>
        <v-layout>
          <v-flex>
            <p>Fill in the required extract information, then select from the table below a selection of cultures to apply these extract values to. This will create extracts linked to your selected cultures.</p>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs6>
            <v-menu
              :close-on-content-click="true"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              min-width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="extract.extraction_date"
                  label="Extraction Date"
                  readonly
                  :rules="fieldRules"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="extract.extraction_date" no-title scrollable :rules="fieldRules">
              </v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs6>
            <CactusAutocomplete
              :multiple="false"
              :api-service="extractionMethodAPI"
              add-new-label="Create new extraction method"
              label="Extraction Method"
              item-text="name"
              :api-items="autoComplete.extractionMethod"
              :add-new-item="true"
              @new="appendExtractionMethod"
              :rules="fieldRules"
              v-model="extract.extraction_method">
            </CactusAutocomplete>
          </v-flex>
          <v-flex xs6>
            <v-text-field label="Solvent Volume" :rules="fieldRules" suffix="ml" type="number" step="0.1" min="0" v-model="extract.extraction_solvent_volume" required></v-text-field>
          </v-flex>
          <v-flex xs6>
            <CactusAutocomplete
              :multiple="false"
              :api-service="extractionSolventAPI"
              add-new-label="Create new extraction solvent"
              label="Extraction Solvent"
              item-text="name"
              :api-items="autoComplete.extractionSolvent"
              :add-new-item="true"
              :rules="fieldRules"
              @new="appendExtractionSolvent"
              v-model="extract.extraction_solvent">
            </CactusAutocomplete>
          </v-flex>
          <v-flex xs12>
            <v-textarea box label="Comments" v-model="extract.comments"></v-textarea>
          </v-flex>
          <v-divider></v-divider>
        </v-layout>
        <v-layout>
          <v-flex>
            <CultureList :items="cultures" :actions="false" v-model="selectedCultures" :selectable="true"></CultureList>
          </v-flex>
        </v-layout>
      </v-container>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="submit" :loading="saving" :disabled="invalid">
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CactusAutocomplete from '@/components/inputs/autocomplete--agnostic.vue'
import ExtractionMethodAPI from '@/api/extraction-method.js'
import ExtractionSolventAPI from '@/api/extraction-solvent.js'
import CultureList from '@/components/culture/table--list-culture.vue'
import CultureAPI from '@/api/culture.js'
import ExtractAPI from '@/api/extract.js'
import {mapState} from 'vuex'

export default {
  props: ['value'],
  components: {
    CactusAutocomplete,
    CultureList
  },
  data () {
    return {
      show: false,
      extract: {
        extraction_date: new Date().toISOString().substr(0, 10),
        extraction_method: undefined,
        extraction_solvent: undefined,
        extraction_solvent_volume: undefined,
        comments: undefined
      },
      autoComplete: {
        extractionMethod: [],
        extractionSolvent: []
      },
      cultures: [],
      selectedCultures: [],
      loading: false,
      saving: false
    }
  },
  computed: {
    invalid () {
      return !this.extract.extraction_method || !this.extract.extraction_date || !this.selectedCultures.length
    },
    ...mapState(['user']),
    cultureFilterParams () {
      /**
       * allows us to configure these parameters to the API say
       * grab different researchers, or different status
       */
      const filterParams = {
        researcher_id: {filter: 'equals', value: this.user.user_details_id},
        status: {filter: 'is', value: 'null'}
      }
      return filterParams
    },
    fieldRules () {
      const rules = []
      if (this.requireFields) {
        rules.push(v => !!v || 'Required')
      }
      return rules
    }
  },
  created () {
    this.extractionMethodAPI = ExtractionMethodAPI
    this.extractionSolventAPI = ExtractionSolventAPI
    this.loadAutocompletes()
    this.getCultures()
  },
  methods: {
    reset () {
      this.selectedCultures = []
    },
    async getCultures () {
      this.loading = true
      const params = {
        'select': '*,researcher(*),culture_medium(*),taxonomy(*),organization(*),experiment_class(*)',
        'order': 'culture_id.desc'
      }

      const {data} = await CultureAPI.get({params, filterParams: this.cultureFilterParams})
      this.cultures = data
      this.loading = false
    },
    async loadAutocompletes () {
      /**
       * cache the auto completes ahead of time.
       */
      const params = {
        limit: 500
      }
      const [extractionMethod, extractionSolvent] = await Promise.all([
        ExtractionMethodAPI.get({params}),
        ExtractionSolventAPI.get({params})
      ])
      this.autoComplete.extractionMethod = extractionMethod.data
      this.autoComplete.extractionSolvent = extractionSolvent.data
    },
    appendExtractionMethod (item) {
      /**
       * if the user creates a new method,
       * its at the server, but not on our end, so
       * append to our list
       */
      this.autoComplete.extractionMethod.push(item)
    },
    appendExtractionSolvent (item) {
      /**
       * if the user creates a new method,
       * its at the server, but not on our end, so
       * append to our list
       */
      this.autoComplete.extractionSolvent.push(item)
    },
    async submit () {
      /**
       * send payload of extract props
       * and selected cultures to the API
       */
      this.saving = true
      const culturePayload = []
      for (let culture of this.selectedCultures) {
        console.log(culture)
        const payload = {
          extract: this.extract,
          culture: culture
        }
        culturePayload.push(payload)
      }
      await ExtractAPI.create(culturePayload)
      this.$emit('save', {})
      this.show = false
    }
  },
  watch: {
    show () {
      if (!this.show) {
        this.reset()
      }
      this.$emit('input', this.show)
    },
    value () {
      this.show = this.value
    }
  }
}
</script>

<style>

</style>
